import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Array, Set } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { Network } from '@root/entities/network/model'
import { Provider } from '@root/entities/provider/model'
import { ProviderPill } from '@root/entities/provider/ui'

import type { ExploreFiltersFormModel } from '../../atoms/reatom-explore-filters-form'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: ExploreFiltersFormModel
} & ClassNameProps

export const ExploreFilterProviders: React.FC<Props> = ({
  className,
  model,
}) => {
  const { t } = useTranslation()
  const [selectedProviders, setSelectedProviders] = useAtom(model.providers)
  const [withMayhem, setWithMayhem] = useAtom(model.withMayhem)

  const [network] = useAtom(selectedNetworkAtom)
  const providers = Provider.providersByNetwork[network]

  const handleToggleProvider = (provider: Provider.Provider) => {
    if (selectedProviders === 'all') {
      const selection = providers.filter((it) => it.id !== provider.id)
      setSelectedProviders(Set.create(selection.map((it) => it.id)))
      return
    }

    const selection = Set.toggle(provider.id)(selectedProviders)
    if (selection.size === providers.length) {
      setSelectedProviders('all')
      return
    }

    setSelectedProviders(selection)
  }

  const handleSelectAll = () => {
    if (selectedProviders === 'all' && withMayhem) {
      setWithMayhem(false)
      setSelectedProviders(Set.create())
      return
    }

    setWithMayhem(true)
    setSelectedProviders('all')
  }

  const renderPill = (provider: Provider.Provider) => {
    const selected = (() => {
      if (provider === Provider.pumpFunMayhem) {
        return withMayhem
      }

      return selectedProviders === 'all' || selectedProviders.has(provider.id)
    })()

    return (
      <ProviderPill
        key={provider.id}
        provider={provider}
        checked={selected}
        onChange={() => {
          if (provider === Provider.pumpFunMayhem) {
            setWithMayhem(!withMayhem)
            return
          }

          handleToggleProvider(provider)
        }}
      />
    )
  }

  const providersSorted = useMemo(() => {
    if (network !== Network.Solana) {
      return providers
    }

    const hasPumpFun = providers.some((it) => it.id === Provider.pumpFun.id)
    const hasPumpSwap = providers.some((it) => it.id === Provider.pumpSwap.id)
    const withMayhem = hasPumpFun || hasPumpSwap

    return Array.compact([
      hasPumpFun ? Provider.pumpFun : undefined,
      hasPumpSwap ? Provider.pumpSwap : undefined,
      withMayhem ? Provider.pumpFunMayhem : undefined,
      ...providers.filter(
        (it) => it.id !== Provider.pumpFun.id && it.id !== Provider.pumpSwap.id,
      ),
    ])
  }, [network, providers])

  return (
    <Root className={className}>
      <Header>
        <Label>{t('protocol_many')}</Label>
        <SelectAllButton type="button" onClick={handleSelectAll}>
          {selectedProviders === 'all'
            ? t('common.unselect-all')
            : t('common.select-all')}
        </SelectAllButton>
      </Header>
      <Pills>{providersSorted.map(renderPill)}</Pills>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 10px;
  padding-block: 8px 4px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Label = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const SelectAllButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;

  color: var(--primary-9);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  cursor: pointer;
  transition: color 0.15s linear;

  &:hover {
    color: var(--primary-10);
  }
`

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`
